import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { setGlobalNavigate } from './services/notifications'
import { prewarmVoiceAudio } from './components/voice/audioContext'
import { prewarmDenoiser } from './services/denoiserBackend'
import { sounds } from './services/sounds'
import { IS_CAPACITOR } from './services/config'
import { useAuthStore } from './stores/authStore'
import { useGuestStore } from './stores/guestStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useChannelStore } from '@/shell/stores/channelStore'
import { useAppShellStore } from './stores/appShellStore'
import { useIsMobile } from './hooks/useBreakpoint'
import MobileNavDrawer from './components/layout/MobileNavDrawer'
import Login from './pages/Login'
import Register from './pages/Register'
import Chat from './pages/Chat'
import AdminPanel from './pages/AdminPanel'
import ServerAdminPage from './pages/ServerAdminPage'
import DepartmentPoliciesPage from './pages/DepartmentPoliciesPage'
import ProbeWorkspacePage from './pages/workspace/ProbeWorkspacePage'
import TrustWorkspacePage from './pages/workspace/TrustWorkspacePage'
import MdmWorkspacePage from './pages/workspace/MdmWorkspacePage'
import ActivityBreakdown from './pages/ActivityBreakdown'
import Activate from './pages/Activate'
import ResetPassword from './pages/ResetPassword'
import ProbeBindPage from './pages/ProbeBindPage'
import SignCeremonyPage from '@os/sign/web/pages/SignCeremonyPage'
import UserProfilePage from './pages/UserProfilePage'
import UserPublicProfilePage from './pages/UserPublicProfilePage'
import InvitePage from './pages/InvitePage'
import GuestJoinPage from './pages/GuestJoinPage'
import OuterCallPage from './pages/OuterCallPage'
import ClientPortalPage from '@os/crm/web/pages/portal/ClientPortalPage'
import UIShowcase from './pages/UIShowcase'
import TwoRailDemo from './pages/admin/design/TwoRailDemo'
import ThreeRailDemo from './pages/admin/design/ThreeRailDemo'
import PeopleWorkspacePage from './pages/workspace/PeopleWorkspacePage'
import TasksLayout from '@os/taskboards/web/pages/TasksLayout'
import TasksOverview from '@os/taskboards/web/pages/TasksOverview'
import TasksBoardsList from '@os/taskboards/web/pages/TasksBoardsList'
import BoardView from '@os/taskboards/web/pages/Board/BoardView'
import BoardSettings from '@os/taskboards/web/pages/Board/BoardSettings'
import SprintsView from '@os/taskboards/web/pages/Board/SprintsView'
import SprintsLanding from '@os/taskboards/web/pages/Board/SprintsLanding'
import SprintDetail from '@os/taskboards/web/pages/Board/SprintDetail'
import BacklogView from '@os/taskboards/web/pages/Board/BacklogView'
import FormsView from '@os/taskboards/web/pages/Board/FormsView'
import FormBuilder from '@os/taskboards/web/pages/Board/FormBuilder'
import FormPreview from '@os/taskboards/web/pages/Board/FormPreview'
import SupportWidget from './components/widgets/SupportWidget'
import TicketPage from '@os/taskboards/web/pages/Public/TicketPage'
import FormSubmissionsList from '@os/taskboards/web/pages/Board/FormSubmissionsList'
import FormPage from '@os/taskboards/web/pages/Public/FormPage'
import EmbedPage from '@os/taskboards/web/pages/Public/EmbedPage'
import WidgetEmbedPage from '@os/taskboards/web/pages/Public/WidgetEmbedPage'
import FieldsTab from '@os/taskboards/web/pages/Board/settings/FieldsTab'
import StatusesTab from '@os/taskboards/web/pages/Board/settings/StatusesTab'
import GroupsTab from '@os/taskboards/web/pages/Board/settings/GroupsTab'
import AccessTab from '@os/taskboards/web/pages/Board/settings/AccessTab'
import IntegrationsTab from '@os/taskboards/web/pages/Board/settings/IntegrationsTab'
import CrmDashboard from '@os/crm/web/pages/CrmDashboard'
import ClientPage from '@os/crm/web/pages/crm/ClientPage'
import EmotionalPulse from './pages/workspace/EmotionalPulse'
import EmotionIncidentView from './pages/workspace/EmotionIncidentView'
import KnowledgeWorkspacePage from './pages/workspace/KnowledgeWorkspacePage'
import NotesWorkspacePage from './pages/workspace/NotesWorkspacePage'
import SignWorkspacePage from './pages/workspace/SignWorkspacePage'
import LearningWorkspacePage from './pages/workspace/LearningWorkspacePage'
import VaultWorkspacePage from './pages/workspace/VaultWorkspacePage'
import FilesWorkspacePage from './pages/workspace/FilesWorkspacePage'
import HrWorkspacePage from './pages/workspace/HrWorkspacePage'
import RequiredLearningGate from './components/RequiredLearningGate'
import MandatoryLearningGate from './components/MandatoryLearningGate'
import AiDashboard from './pages/workspace/AiDashboard'
import Dashboards from './components/desktop/Dashboards'
import MailDashboard from './pages/workspace/MailDashboard'
import CalendarWorkspacePage from './pages/workspace/CalendarWorkspacePage'
import BookingPagesPage from './pages/BookingPagesPage'
import PublicBookingPage from './pages/PublicBookingPage'
import PublicBookingCancelPage from './pages/PublicBookingCancelPage'
import RecordingsPage from './components/voice/RecordingsPage'
import VoiceSessionHistoryPage from './components/voice/history/VoiceSessionHistoryPage'
import ScreenOverlayRoute from './pages/_screen-overlay'
import ModalManager from './components/modals/ModalManager'
import ToastContainer from './components/toast/ToastContainer'
import DialogHost from './components/dialog/DialogHost'
import LiveKitBackgroundAudio from './components/voice/LiveKitBackgroundAudio'
import VoiceManager from './components/voice/VoiceManager'
import TrackerHeartbeat from './components/tracker/TrackerHeartbeat'
import ActiveCallBar from '@os/messenger/web/components/calls/ActiveCallBar'
import DmCallSplash from '@os/messenger/web/components/calls/DmCallSplash'
import ErrorBoundary from './components/ErrorBoundary'
import UpdateAvailableBadge from './components/UpdateAvailableBadge'
import ElectronDailyReload from './components/ElectronDailyReload'
import SplashScreen from './components/SplashScreen'
import { useDmCallSounds } from './hooks/useDmCallSounds'
import { useBirthdayBoot } from './components/birthday/useBirthdayBoot'

// Tracker module — Hubstaff mirror, lazy-loaded so the main bundle
// doesn't pay for it until an admin opens /tracker.
const TrackerLayout = lazy(() => import('@os/tracker/web/components/TrackerLayout'))
const TrackerIndex = lazy(() => import('@os/tracker/web/pages/index'))
// People
const MembersListPage = lazy(() => import('@os/tracker/web/pages/people/members'))
const MemberDetailPage = lazy(() => import('@os/tracker/web/pages/people/members/[id]'))
const TeamDetailPage = lazy(() => import('@os/tracker/web/pages/people/teams/[id]'))
// Project management
const ProjectsListPage = lazy(() => import('@os/tracker/web/pages/project-management/projects'))
const ProjectDetailPage = lazy(() => import('@os/tracker/web/pages/project-management/projects/[id]'))
const ClientsPage = lazy(() => import('@os/tracker/web/pages/project-management/clients'))
// Dashboard / Timesheets / Activity
const TrackerDashboardPage = lazy(() => import('@os/tracker/web/pages/dashboard'))
const TimesheetsViewEditPage = lazy(() => import('@os/tracker/web/pages/timesheets/view-edit'))
const TimesheetsApprovalsPage = lazy(() => import('@os/tracker/web/pages/timesheets/approvals'))
const ScreenshotsPage = lazy(() => import('@os/tracker/web/pages/activity/screenshots'))
const AppActivityPage = lazy(() => import('@os/tracker/web/pages/activity/apps'))
const UrlActivityPage = lazy(() => import('@os/tracker/web/pages/activity/urls'))
// Insights
const HighlightsPage = lazy(() => import('@os/tracker/web/pages/insights/highlights'))
const PerformancePage = lazy(() => import('@os/tracker/web/pages/insights/performance'))
const TimelinePage = lazy(() => import('@os/tracker/web/pages/insights/timeline'))
const UnusualActivityPage = lazy(() => import('@os/tracker/web/pages/insights/unusual-activity'))
const SmartNotificationsPage = lazy(() => import('@os/tracker/web/pages/insights/smart-notifications'))
const OutputPage = lazy(() => import('@os/tracker/web/pages/insights/output'))
// Calendar / Reports / Financials / Silent app
const ReportsPage = lazy(() => import('@os/tracker/web/pages/reports'))
const DailyReportPage = lazy(() => import('@os/tracker/web/pages/reports/daily'))
const WeeklyReportPage = lazy(() => import('@os/tracker/web/pages/reports/weekly'))
const ActivityReportPage = lazy(() => import('@os/tracker/web/pages/reports/activity'))
const TimeAmountsReportPage = lazy(() => import('@os/tracker/web/pages/reports/time-amounts'))
const TimeEntriesReportPage = lazy(() => import('@os/tracker/web/pages/reports/time-entries'))
const UnavailableReportPage = lazy(() => import('@os/tracker/web/pages/reports/_unavailable'))
const ProjectBudgetReportPage = lazy(() => import('@os/tracker/web/pages/reports/project-budget'))
const ClientTimePage = lazy(() => import('@os/tracker/web/pages/client-time'))
const FinancialsPage = lazy(() => import('@os/tracker/web/pages/financials'))
// Settings tree
const SettingsIndexPage = lazy(() => import('@os/tracker/web/pages/settings/index'))
const OrganizationLayout = lazy(() => import('@os/tracker/web/components/settings/OrganizationLayout'))
const CompanyInformationPage = lazy(() => import('@os/tracker/web/pages/settings/organization/company-information'))
const SecurityLoginPage = lazy(() => import('@os/tracker/web/pages/settings/organization/security-login'))
const ProjectsTodosPage = lazy(() => import('@os/tracker/web/pages/settings/organization/projects-todos'))
const PermissionsPage = lazy(() => import('@os/tracker/web/pages/settings/organization/permissions'))
const SettingsCustomFieldsPage = lazy(() => import('@os/tracker/web/pages/settings/members/custom-fields'))
const SettingsWorkTimeLimitsPage = lazy(() => import('@os/tracker/web/pages/settings/members/work-time-limits'))
const SettingsMemberPaymentsPage = lazy(() => import('@os/tracker/web/pages/settings/members/payments'))
const SettingsAchievementsPage = lazy(() => import('@os/tracker/web/pages/settings/members/achievements'))
const SettingsScheduleCalendarPage = lazy(() => import('@os/tracker/web/pages/settings/schedules/calendar'))
const SettingsActivityPage = lazy(() => import('@os/tracker/web/pages/settings/activity-tracking/activity'))
const SettingsTimesheetsPage = lazy(() => import('@os/tracker/web/pages/settings/activity-tracking/timesheets'))
const SettingsTimeTrackingPage = lazy(() => import('@os/tracker/web/pages/settings/activity-tracking/time-tracking'))
const SettingsScreenshotsPage = lazy(() => import('@os/tracker/web/pages/settings/activity-tracking/screenshots'))
const SettingsAppsUrlsClassificationsPage = lazy(() => import('@os/tracker/web/pages/settings/insights/apps-urls-classifications'))
const SettingsRemoteVsInOfficePage = lazy(() => import('@os/tracker/web/pages/settings/insights/remote-vs-in-office'))
const SettingsTimeOffPage = lazy(() => import('@os/tracker/web/pages/settings/policies/time-off'))
const SettingsWorkBreaksPage = lazy(() => import('@os/tracker/web/pages/settings/policies/work-breaks'))
const SettingsOvertimePage = lazy(() => import('@os/tracker/web/pages/settings/policies/overtime'))
const SettingsAllIntegrationsPage = lazy(() => import('@os/tracker/web/pages/settings/integrations/all-integrations'))
const SettingsWisePage = lazy(() => import('@os/tracker/web/pages/settings/integrations/wise'))
const SettingsJiraPage = lazy(() => import('@os/tracker/web/pages/settings/integrations/jira'))
const SettingsSlackPage = lazy(() => import('@os/tracker/web/pages/settings/integrations/slack'))

// Public shared-note viewer (no auth) — lazy so guests opening a share link
// don't pay for it unless they hit /shared/:token, and logged-in users never
// load it at all.
const SharedNotePage = lazy(() => import('@os/notes/web/pages/SharedNotePage'))

// HR public surfaces (no auth) — jobs board, vacancy detail, candidate
// assessment. Lazy so the main bundle never pays for them.
const JobsIndexPage = lazy(() => import('@os/hr/web/pages/Public/JobsIndexPage'))
const HrVacancyPage = lazy(() => import('@os/hr/web/pages/Public/VacancyPage'))
const AssessPage = lazy(() => import('@os/hr/web/pages/Public/AssessPage'))
// HR Phase-2 candidate lifecycle public surfaces (interview booking /
// self-serve cancel / finalist details form) — capability tokens are the
// credential, same family as /assess/:token.
const InterviewBookingPage = lazy(() => import('@os/hr/web/pages/Public/InterviewBookingPage'))
const InterviewCancelPage = lazy(() => import('@os/hr/web/pages/Public/InterviewCancelPage'))
const CandidateDetailsPage = lazy(() => import('@os/hr/web/pages/Public/CandidateDetailsPage'))

function App() {
  const { isAuthenticated, isLoading, fetchUser } = useAuthStore()
  const { isGuest, serverId: guestServerId, channelId: guestChannelId } = useGuestStore()
  const serversLoaded = useServerStore((s) => s.serversLoaded)
  const fetchServers = useServerStore((s) => s.fetchServers)
  // Client-First bootstrap: while fetchUser() is in-flight we trust the
  // presence of an auth token in localStorage and render the app shell
  // immediately. If the token turns out to be invalid, fetchUser drops
  // isAuthenticated and the route guards send the user to /login. This
  // removes the previous full-screen "Loading…" flash on every reload.
  const hasAuthToken =
    typeof window !== 'undefined' &&
    (!!localStorage.getItem('token') || !!localStorage.getItem('guestToken'))
  const canAccessApp = isAuthenticated || isGuest || (isLoading && hasAuthToken)
  // Mobile shell switch (<1024). Drives the single persistent MobileNavDrawer
  // mounted in render below.
  const isMobile = useIsMobile()

  useDmCallSounds()
  useBirthdayBoot(isAuthenticated)

  // Register the react-router navigate fn with the notifications module
  // so notification-click handlers route through the router instead of
  // the unreliable history.pushState + popstate dance. Re-runs whenever
  // navigate's identity changes (it's stable across renders, so this is
  // effectively a one-shot mount install).
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    setGlobalNavigate(navigate)
    return () => setGlobalNavigate(() => {})
  }, [navigate])

  useEffect(() => {
    fetchUser()
    // Ask the OS for notification permission early — once per session,
    // not at the moment a call comes in (the prompt-then-miss-the-call
    // race was annoying). Electron auto-grants on most setups; browsers
    // surface a one-shot dialog.
    import('./services/notifications').then((m) => {
      m.requestNotificationPermission().catch(() => {})
    }).catch(() => {})
  }, [])

  // Pre-warm the voice stack the moment the user is authenticated — this
  // covers BOTH a fresh login AND a returning session restored from a
  // localStorage token by fetchUser() (the common daily case, where the
  // Login form never renders). Without it the denoiser + audio context
  // only warm lazily on first channel join, so the very first "go live"
  // pays full cold-start. Mic-free and idempotent; fire-and-forget.
  //   - prewarmDenoiser: RNNoise WASM/worklet (+ resident DFN worker if
  //     the user prefers DFN) — needs no user gesture.
  //   - prewarmVoiceAudio: create the shared playback AudioContext now so
  //     the global gesture-unlock listener can resume it on the user's
  //     first click, well before they join (Login.tsx also does this
  //     synchronously under the submit gesture for the fresh-login case).
  const prewarmEligible = isAuthenticated || isGuest
  useEffect(() => {
    if (!prewarmEligible) return
    prewarmVoiceAudio()
    void prewarmDenoiser()
  }, [prewarmEligible])

  // Kick the initial servers fetch as soon as auth resolves so the
  // splash gate below can lift. fetchServers is otherwise triggered
  // by deep child components on mount which left a window where
  // PrimarySidebar rendered with default-true enabledModules (all
  // modules visible) before flipping to the filtered subset —
  // visible flicker on every reload. Idempotent: guarded by the
  // serversLoaded flag the store itself flips on success or failure.
  useEffect(() => {
    if (!isAuthenticated) return
    if (serversLoaded) return
    fetchServers().catch(() => {})
  }, [isAuthenticated, serversLoaded, fetchServers])

  // Load the admin-configured system-sound overrides once authenticated.
  // Failure is non-fatal (sounds.ts falls back to built-in defaults).
  useEffect(() => {
    if (!isAuthenticated) return
    void sounds.loadConfig()
  }, [isAuthenticated])

  // Show the brand splash for the whole boot window of a returning
  // (token-bearing) session — that's:
  //   1) while fetchUser is still in-flight (isLoading + hasAuthToken),
  //      before isAuthenticated even flips. Without this the routes
  //      render Chat optimistically against `canAccessApp` and the
  //      rail paints with default-true enabledModules — visible flash.
  //   2) after fetchUser resolves but before the servers list lands.
  // The HTML-level #boot-splash in index.html covers the millisecond
  // window before React mounts; this <SplashScreen /> takes over the
  // moment <App /> renders and stays until the boot finishes. Guests
  // and the no-token-at-all flow (login form) skip both because
  // hasAuthToken === false there.
  // The screen-share annotation overlay is a separate Electron BrowserWindow
  // that loads this same SPA at /_/screen-overlay. On lite / web-shell builds
  // that means a SECOND full app instance for the same user, sharing the
  // Electron default session. It must render ONLY the transparent annotation
  // canvas — never the app shell. In particular VoiceManager (mounted below)
  // auto-rejoins the user's voice channel with `takeover: true`; from this
  // second window the server then tells the REAL (sharing) window "you joined
  // on another device", tears down its LiveKit room, and the share never lands
  // (the overlay window itself lingers). Short-circuit here so the overlay
  // window never becomes a second voice device / call participant / redirect
  // target. location.pathname is build-agnostic (browser- or hash-router).
  if (location.pathname === '/_/screen-overlay') {
    return <ScreenOverlayRoute />
  }

  const bootstrapping =
    hasAuthToken && ((isLoading && !isAuthenticated) || (isAuthenticated && !serversLoaded))
  if (bootstrapping) {
    return <SplashScreen />
  }

  return (
    <ErrorBoundary>
      {/* Mounted ONCE at App root so the /version poll + critical-countdown
          persist across navigation and the desktop pill covers every route.
          On mobile it self-portals its in-flow strip into AppLayout's shell. */}
      <UpdateAvailableBadge />
      {/* Desktop-only: a live-site Electron shell left running overnight
          hard-reloads at 01:00 local so it never drifts behind web deploys. */}
      <ElectronDailyReload />
      {/* Persistent mobile nav drawer — mounted ONCE here (not per-page in
          AppLayout) so it survives route changes without re-animating open.
          Each route's AppLayout publishes its L2 list into appShellStore. */}
      {canAccessApp && isMobile && <MobileNavDrawer />}
      <Routes>
        {/* Standalone overlay window — no AppShell, no auth redirect */}
        <Route path="/_/screen-overlay" element={<ScreenOverlayRoute />} />
        <Route path="/login" element={!canAccessApp ? <Login /> : <LoginAlreadyAuthed />} />
        <Route path="/activate" element={<Activate />} />
        {/* Self-service password reset — public, like /activate. The user
            lands here from the emailed link (?token=…), sets a new
            password, then re-logs in (no auto-login / JWT issued). */}
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Probe pairing landing — public (no auth). The invite email links
            here (buildBindUrl → /probe/bind?t=<token>); the page fires the
            rx2go-probe:// deep link or offers the installer download. */}
        <Route path="/probe/bind" element={<ProbeBindPage />} />
        {/* Sign ceremony landing — public (no auth). Recipients open this page
            from the sign.request email; the page loads the document for signing
            and handles OTP verification + field completion. */}
        <Route path="/sign/:token" element={<SignCeremonyPage />} />
        {/* Shared note landing — public (no auth). Recipients open this page
            from a copied share link (/shared/<token>); the page resolves the
            token server-side and renders a read-only or editable note view. */}
        <Route path="/shared/:token" element={<Suspense fallback={null}><SharedNotePage /></Suspense>} />
        {/* A department registration link is `/register?invite=CODE`. An
            already-logged-in user who opens it should NOT see the signup
            form (that creates a duplicate account) — bounce them into the
            app. Gate on `isAuthenticated`, not `canAccessApp`: a guest must
            still be able to convert to a real account via this form. */}
        <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/" replace />} />
        <Route path="/channels/@me" element={canAccessApp ? <Chat /> : <Navigate to="/login" />} />
        <Route path="/channels/:channelId" element={canAccessApp ? <Chat /> : <Navigate to="/login" />} />
        <Route path="/servers/:serverId/channels/:channelId" element={canAccessApp ? <Chat /> : <Navigate to="/login" />} />
        {/* Admin is web-only: the mobile (Capacitor) build ships no admin/billing/
            telephony UI — all administration happens on the web. Hard-gate the route
            so the page can't render even via a direct/deep link. */}
        <Route path="/admin" element={IS_CAPACITOR ? <Navigate to="/" replace /> : isAuthenticated ? <AdminPanel /> : <Navigate to="/login" />} />
        <Route path="/admin/people" element={<Navigate to="/admin?tab=module:people" replace />} />
        <Route path="/workspace/probe" element={canAccessApp ? <ProbeWorkspacePage /> : <Navigate to="/login" />} />
        <Route path="/workspace/trust/*" element={canAccessApp ? <TrustWorkspacePage /> : <Navigate to="/login" />} />
        <Route path="/workspace/mdm/*" element={canAccessApp ? <MdmWorkspacePage /> : <Navigate to="/login" />} />
        <Route path="/reports/time" element={canAccessApp ? <ActivityBreakdown /> : <Navigate to="/login" />} />
        <Route path="/admin/design/two-rail" element={isAuthenticated ? <TwoRailDemo /> : <Navigate to="/login" />} />
        <Route path="/admin/design/three-rail" element={isAuthenticated ? <ThreeRailDemo /> : <Navigate to="/login" />} />
        <Route path="/admin/design/primitives" element={isAuthenticated ? <UIShowcase /> : <Navigate to="/login" />} />
        <Route path="/servers/:serverId/admin" element={IS_CAPACITOR ? <Navigate to="/" replace /> : isAuthenticated ? <ServerAdminPage /> : <Navigate to="/login" />} />
        <Route path="/servers/:serverId/departments/:deptId/policies" element={IS_CAPACITOR ? <Navigate to="/" replace /> : isAuthenticated ? <DepartmentPoliciesPage /> : <Navigate to="/login" />} />
        <Route path="/profile" element={isAuthenticated ? <UserProfilePage /> : <Navigate to="/login" />} />
        <Route path="/users/:userId" element={isAuthenticated ? <UserPublicProfilePage /> : <Navigate to="/login" />} />
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/join/:code" element={<GuestJoinPage />} />
        <Route path="/call/:code" element={<OuterCallPage />} />
        <Route path="/__ui" element={<UIShowcase />} />
        {/* Workspace dashboards — vision prototype with mock data. */}
        <Route path="/workspace"           element={canAccessApp ? <Navigate to="/workspace/people" replace /> : <Navigate to="/login" />} />
        <Route path="/workspace/people"    element={canAccessApp ? <PeopleWorkspacePage />    : <Navigate to="/login" />} />
        <Route path="/workspace/hr/:tab?"  element={canAccessApp ? <HrWorkspacePage />        : <Navigate to="/login" />} />
        <Route path="/workspace/tasks" element={canAccessApp ? <TasksLayout /> : <Navigate to="/login" />}>
          <Route index element={<TasksOverview />} />
          <Route path="boards" element={<TasksBoardsList />} />
          <Route path="boards/:boardId" element={<BoardView />} />
          <Route path="boards/:boardId/sprints" element={<SprintsView />}>
            <Route index element={<SprintsLanding />} />
            <Route path=":sprintId" element={<SprintDetail />} />
          </Route>
          <Route path="boards/:boardId/backlog" element={<BacklogView />} />
          <Route path="boards/:boardId/forms" element={<FormsView />} />
          <Route path="boards/:boardId/forms/:formId" element={<FormBuilder />} />
          <Route path="boards/:boardId/forms/:formId/preview" element={<FormPreview />} />
          <Route path="boards/:boardId/forms/:formId/submissions" element={<FormSubmissionsList />} />
          <Route path="boards/:boardId/settings" element={<BoardSettings />}>
            <Route index element={<Navigate to="fields" replace />} />
            <Route path="fields" element={<FieldsTab />} />
            <Route path="statuses" element={<StatusesTab />} />
            <Route path="groups" element={<GroupsTab />} />
            {/* OSPROD-235: Members + Sharing merged into one Access tab. Old
                paths redirect so existing deep-links/bookmarks keep working. */}
            <Route path="access" element={<AccessTab />} />
            <Route path="members" element={<Navigate to="../access" replace />} />
            <Route path="sharing" element={<Navigate to="../access" replace />} />
            <Route path="integrations" element={<IntegrationsTab />} />
          </Route>
        </Route>
        <Route path="/workspace/crm"       element={canAccessApp ? <CrmDashboard />       : <Navigate to="/login" />} />
        <Route path="/workspace/crm/clients/:id" element={canAccessApp ? <ClientPage /> : <Navigate to="/login" />} />
        <Route path="/workspace/service"   element={canAccessApp ? <EmotionalPulse />      : <Navigate to="/login" />} />
        <Route path="/workspace/service/incident/:id" element={canAccessApp ? <EmotionIncidentView /> : <Navigate to="/login" />} />
        <Route path="/workspace/mail"      element={canAccessApp ? <MailDashboard />      : <Navigate to="/login" />} />
        <Route path="/workspace/calendar"  element={canAccessApp ? <CalendarWorkspacePage />  : <Navigate to="/login" />} />
        {/* OSPROD-BOOK: owner manages booking links here. Public
            visitor surface is mounted UNAUTHENTICATED below. */}
        <Route path="/calendar/booking-pages" element={canAccessApp ? <BookingPagesPage /> : <Navigate to="/login" />} />
        <Route path="/booking/cancel/:token" element={<PublicBookingCancelPage />} />
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        {/* CRM external client portal — public, no internal auth required */}
        <Route path="/portal" element={<ClientPortalPage />} />
        <Route path="/form/:slug" element={<FormPage />} />
        <Route path="/embed/:slug" element={<EmbedPage />} />
        {/* External support widget host — what widget.js opens in its
            overlay iframe on third-party sites. Anonymous. */}
        <Route path="/widget/:slug" element={<WidgetEmbedPage />} />
        {/* Submitter-facing ticket thread — the link every ticket email
            carries. Anonymous; the unguessable token is the credential. */}
        <Route path="/ticket/:token" element={<TicketPage />} />
        {/* HR public surfaces — anonymous. /jobs = published vacancies board,
            /jobs/:slug = vacancy detail + apply, /assess/:token = candidate
            assessment (the unguessable token is the credential). */}
        <Route path="/jobs" element={<Suspense fallback={null}><JobsIndexPage /></Suspense>} />
        <Route path="/jobs/:slug" element={<Suspense fallback={null}><HrVacancyPage /></Suspense>} />
        <Route path="/assess/:token" element={<Suspense fallback={null}><AssessPage /></Suspense>} />
        {/* HR Phase-2 lifecycle: /interview/:token = slot booking (bookingToken),
            /interview/cancel/:token = candidate self-serve cancel (cancelToken),
            /candidate-details/:token = finalist details form (detailsToken).
            Router ranks the static /cancel/ segment above :token, so order
            here doesn't matter. */}
        <Route path="/interview/:token" element={<Suspense fallback={null}><InterviewBookingPage /></Suspense>} />
        <Route path="/interview/cancel/:token" element={<Suspense fallback={null}><InterviewCancelPage /></Suspense>} />
        <Route path="/candidate-details/:token" element={<Suspense fallback={null}><CandidateDetailsPage /></Suspense>} />
        <Route path="/recordings"          element={canAccessApp ? <RecordingsPage />    : <Navigate to="/login" />} />
        <Route path="/recordings/:channelId" element={canAccessApp ? <RecordingsPage />  : <Navigate to="/login" />} />
        <Route path="/channels/:channelId/history" element={canAccessApp ? <VoiceSessionHistoryPage /> : <Navigate to="/login" />} />
        {/* Finance is a hidden vision mock (no backend) — redirect away. */}
        <Route path="/workspace/finance"   element={<Navigate to="/workspace/crm" replace />} />
        <Route path="/workspace/knowledge" element={canAccessApp ? <KnowledgeWorkspacePage /> : <Navigate to="/login" />} />
        <Route path="/workspace/notes"     element={canAccessApp ? <NotesWorkspacePage />    : <Navigate to="/login" />} />
        <Route path="/workspace/sign"      element={canAccessApp ? <SignWorkspacePage />     : <Navigate to="/login" />} />
        <Route path="/workspace/learning"  element={canAccessApp ? <LearningWorkspacePage />  : <Navigate to="/login" />} />
        <Route path="/workspace/vault"     element={canAccessApp ? <VaultWorkspacePage />     : <Navigate to="/login" />} />
        <Route path="/workspace/files"     element={canAccessApp ? <FilesWorkspacePage />     : <Navigate to="/login" />} />
        <Route path="/workspace/ai"        element={canAccessApp ? <AiDashboard />        : <Navigate to="/login" />} />
        {/* Dashboards — /dashboards/:tab gives the section a real URL instead of
            rendering inside Chat at /channels/@me. The component wraps itself in
            AppLayout (same as MailDashboard, CalendarWorkspacePage, etc.). */}
        <Route path="/dashboards" element={canAccessApp ? <Navigate to="/dashboards/home" replace /> : <Navigate to="/login" />} />
        <Route path="/dashboards/:tab" element={canAccessApp ? <Dashboards /> : <Navigate to="/login" />} />
        {/* Tracker module — Hubstaff mirror, admin-only. canAccessApp
            (loose check) is intentional: hard-loading /tracker/<page>
            during fetchUser() would otherwise redirect to /login →
            LoginAlreadyAuthed → / and leave a blank screen. */}
        <Route
          path="/tracker/*"
          element={
            canAccessApp ? (
              <Suspense fallback={null}>
                <Routes>
                  <Route element={<TrackerLayout />}>
                    <Route index element={<TrackerIndex />} />
                    {/* People */}
                    <Route path="people/members" element={<MembersListPage />} />
                    <Route path="people/members/:id" element={<MemberDetailPage />} />
                    <Route path="people/teams/:id" element={<TeamDetailPage />} />
                    {/* Project management */}
                    <Route path="project-management/projects" element={<ProjectsListPage />} />
                    <Route path="project-management/projects/:id" element={<ProjectDetailPage />} />
                    <Route path="project-management/clients" element={<ClientsPage />} />
                    {/* Dashboard / Timesheets / Activity */}
                    <Route path="dashboard" element={<TrackerDashboardPage />} />
                    <Route path="timesheets/view-edit" element={<TimesheetsViewEditPage />} />
                    <Route path="timesheets/approvals" element={<TimesheetsApprovalsPage />} />
                    <Route path="activity/screenshots" element={<ScreenshotsPage />} />
                    <Route path="activity/apps" element={<AppActivityPage />} />
                    <Route path="activity/urls" element={<UrlActivityPage />} />
                    {/* Insights */}
                    <Route path="insights/highlights" element={<HighlightsPage />} />
                    <Route path="insights/performance" element={<PerformancePage />} />
                    <Route path="insights/timeline" element={<TimelinePage />} />
                    <Route path="insights/unusual-activity" element={<UnusualActivityPage />} />
                    <Route path="insights/smart-notifications" element={<SmartNotificationsPage />} />
                    <Route path="insights/output" element={<OutputPage />} />
                    {/* Calendar / Reports / Financials / Silent app */}
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="reports/daily" element={<DailyReportPage />} />
                    <Route path="reports/weekly" element={<WeeklyReportPage />} />
                    <Route path="reports/activity" element={<ActivityReportPage />} />
                    <Route path="reports/time-amounts" element={<TimeAmountsReportPage />} />
                    <Route path="reports/time-entries" element={<TimeEntriesReportPage />} />
                    <Route path="reports/time-and-activity" element={<DailyReportPage />} />
                    <Route path="reports/apps-urls" element={
                      <UnavailableReportPage type="apps-urls" title="Apps & URLs"
                        description="Application and website usage breakdown." />
                    } />
                    <Route path="reports/project-budget" element={<ProjectBudgetReportPage />} />
                    <Route path="reports/project-schedule" element={
                      <UnavailableReportPage type="project-schedule" title="Schedule report"
                        description="Schedule adherence per project." />
                    } />
                    <Route path="reports/schedule-shifts" element={
                      <UnavailableReportPage type="schedule-shifts" title="Shifts"
                        description="Scheduled vs tracked shifts." />
                    } />
                    <Route path="reports/manual-time" element={
                      <UnavailableReportPage type="manual-time" title="Manual time"
                        description="Manually-added time entries." />
                    } />
                    <Route path="reports/custom" element={
                      <UnavailableReportPage type="custom" title="Custom"
                        description="Custom report builder." />
                    } />
                    <Route path="client-time" element={<ClientTimePage />} />
                    <Route path="financials" element={<FinancialsPage />} />
                    {/* Settings tree */}
                    <Route path="settings" element={<SettingsIndexPage />} />
                    <Route path="settings/organization" element={<OrganizationLayout />}>
                      <Route index element={<Navigate to="company-information" replace />} />
                      <Route path="company-information" element={<CompanyInformationPage />} />
                      <Route path="security-login" element={<SecurityLoginPage />} />
                      <Route path="projects-todos" element={<ProjectsTodosPage />} />
                      <Route path="permissions" element={<PermissionsPage />} />
                    </Route>
                    <Route path="settings/members/custom-fields" element={<SettingsCustomFieldsPage />} />
                    <Route path="settings/members/work-time-limits" element={<SettingsWorkTimeLimitsPage />} />
                    <Route path="settings/members/payments" element={<SettingsMemberPaymentsPage />} />
                    <Route path="settings/members/achievements" element={<SettingsAchievementsPage />} />
                    <Route path="settings/schedules/calendar" element={<SettingsScheduleCalendarPage />} />
                    <Route path="settings/activity-tracking/activity" element={<SettingsActivityPage />} />
                    <Route path="settings/activity-tracking/timesheets" element={<SettingsTimesheetsPage />} />
                    <Route path="settings/activity-tracking/time-tracking" element={<SettingsTimeTrackingPage />} />
                    <Route path="settings/activity-tracking/screenshots" element={<SettingsScreenshotsPage />} />
                    <Route path="settings/insights/apps-urls-classifications" element={<SettingsAppsUrlsClassificationsPage />} />
                    <Route path="settings/insights/remote-vs-in-office" element={<SettingsRemoteVsInOfficePage />} />
                    <Route path="settings/policies/time-off" element={<SettingsTimeOffPage />} />
                    <Route path="settings/policies/work-breaks" element={<SettingsWorkBreaksPage />} />
                    <Route path="settings/policies/overtime" element={<SettingsOvertimePage />} />
                    <Route path="settings/integrations/all-integrations" element={<SettingsAllIntegrationsPage />} />
                    <Route path="settings/integrations/wise" element={<SettingsWisePage />} />
                    <Route path="settings/integrations/jira" element={<SettingsJiraPage />} />
                    <Route path="settings/integrations/slack" element={<SettingsSlackPage />} />
                    <Route path="*" element={<TrackerIndex />} />
                  </Route>
                </Routes>
              </Suspense>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/" element={
          canAccessApp
            ? (isGuest && guestServerId && guestChannelId
                ? <Navigate to={`/servers/${guestServerId}/channels/${guestChannelId}`} />
                : <RootLanding />)
            : <Navigate to="/login" />
        } />
        {/* Catch-all for URLs no route matches (e.g. /calendar instead of
            /workspace/calendar). Without it the router rendered NOTHING —
            a blank white page that reads as "prod is down". */}
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
      <ModalManager />
      <ToastContainer />
      <DialogHost />
      <LiveKitBackgroundAudio />
      {/* VoiceManager lifted from Chat to App level so the
          voice-state listeners + socket-reconnect rejoin handler
          stay mounted while the user navigates to /workspace/mail,
          /workspace/calendar, /tracker/*, etc. The previous Chat-only
          mount meant any route change would unmount the listener,
          and on the next socket reconnect no `voice:join` would re-fire,
          so the server's voice-ghost-killer cron (every minute) would
          drop the user's VoiceState within ~60s. Gated on canAccessApp
          so the component doesn't try to use the socket on /login. */}
      {canAccessApp && <VoiceManager />}
      {isAuthenticated && <TrackerHeartbeat />}
      {/* ActiveCallBar lifted to App level (same rationale as VoiceManager
          above) so the floating minimised-call bar persists across ALL
          tabs — including the module-owned ones (Calendar, Tracker, …)
          whose pages render their own layout and do NOT wrap in os-core's
          AppLayout. Mounted here it self-hides on the active call's own
          route and renders nothing when no call is active. */}
      {canAccessApp && <ActiveCallBar />}
      {/* Mobile-only full-screen incoming/outgoing DM-call splash. App-level
          (like ActiveCallBar) so it rings on ANY screen; self-hides on
          desktop and when there's no incoming/outgoing DM call. */}
      {canAccessApp && <DmCallSplash />}
      {canAccessApp && <LastNonCallPathTracker />}
      {canAccessApp && <RequiredLearningGate />}
      {/* Floating "Report a problem" button (bottom-right). Renders only
          when at least one published taskboards form is widget-enabled
          and visible to this user — see SupportWidget. */}
      {canAccessApp && <SupportWidget />}
      {/* Hard gate: polls required-learning status + redirects non-admins
          with incomplete mandatory courses back to /workspace/learning.
          PrimarySidebar reads the same store to hide every other section. */}
      {canAccessApp && <MandatoryLearningGate />}
    </ErrorBoundary>
  )
}

// OSPROD-63: capture the URL the user is on whenever it's NOT a voice
// channel route. The Minimize button reads this back so clicking
// "collapse to mini-window" drops the user where they came from
// (Tracker page, mail, a text channel, a DM, …) instead of force-
// parking them on the Desktop dashboard. We classify routes by
// `channelStore.activeChannelType` — Chat.tsx populates it from
// serverStore on every channel change, and it's 'VOICE' only while
// the URL points at a voice channel. Auth screens are excluded so a
// post-logout navigation can't poison the target.
function LastNonCallPathTracker() {
  const location = useLocation()
  const activeChannelType = useChannelStore((s) => s.activeChannelType)
  const setLastNonCallPath = useAppShellStore((s) => s.setLastNonCallPath)
  useEffect(() => {
    if (activeChannelType === 'VOICE') return
    const path = location.pathname
    if (path === '/login' || path === '/register' || path.startsWith('/invite/') || path.startsWith('/join/')) return
    setLastNonCallPath(path + location.search)
  }, [location.pathname, location.search, activeChannelType, setLastNonCallPath])
  return null
}

// Deliberately dumb: no data fetching, no auth logic — just say the
// page doesn't exist and offer the way home. "/" resolves the rest
// itself (login redirect for anonymous users, default landing for
// authenticated ones), so this stays safe on every code path.
function NotFoundRoute() {
  const location = useLocation()
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground px-6 text-center">
      <div className="text-5xl font-semibold">404</div>
      <div className="text-lg font-medium">Page not found</div>
      <div className="text-sm text-muted-foreground break-all">
        Nothing lives at <span className="font-mono">{location.pathname}</span>
      </div>
      <Link
        to="/"
        className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition duration-150 hover:opacity-90"
      >
        Go home
      </Link>
    </div>
  )
}

// `/login` while already authenticated normally bounces to `/`. If
// the link carried `?redirect=X` (e.g. coming back from a `/join/CODE`
// "I have an account → Log In" flow), preserve it so the user lands
// on the page they wanted instead of being silently dumped into their
// last-visited Business-OS chat by RootLanding. The same applies when
// the user is logged in and clicks an `/invite/:code` server-invite
// link: we want to *show them the join screen*, not skip it.
function LoginAlreadyAuthed() {
  const [params] = useSearchParams()
  const redirect = params.get('redirect')
  // Only honor same-origin paths to avoid an open-redirect surface.
  // `Navigate` accepts a relative path; reject anything that looks
  // absolute or protocol-prefixed.
  const safe = redirect && redirect.startsWith('/') && !redirect.startsWith('//')
    ? redirect
    : '/'
  return <Navigate to={safe} replace />
}

// "/" landing — the Home dashboard is the product's home screen (2026-07-06
// founder decision). The Business OS rail icon still restores lastBusinessPath.
function RootLanding() {
  const setSection = useAppShellStore((s) => s.setSection)
  useEffect(() => { setSection('dashboards') }, [setSection])
  return <Navigate to="/dashboards/home" replace />
}

export default App
